import React from "react";
import { size, sumBy } from "lodash";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AiccFeedbackDailyPoint } from "types/model/aiccCustomer";
import ChartCard from "./ChartCard";
import TooltipBox from "./TooltipBox";
import { formatAxisDate, formatTooltipDate } from "./formatChartDate";
import {
  AXIS_STROKE,
  AXIS_TICK,
  BAR_MAX_SIZE,
  BAR_RADIUS,
  CHART_COLORS,
  CHART_HEIGHT,
  CHART_MARGIN,
  GRID_STROKE,
  HOVER_CURSOR_FILL,
} from "./chartTheme";
import styles from "./charts.module.scss";

interface Props {
  data: AiccFeedbackDailyPoint[];
  isLoading?: boolean;
}

const SERIES = [
  { dataKey: "thumbsUp" as const, name: "👍 Thumbs Up", color: CHART_COLORS.thumbsUp },
  { dataKey: "thumbsDown" as const, name: "👎 Thumbs Down", color: CHART_COLORS.thumbsDown },
];

function TrendChart({ data, isLoading }: Props) {
  const hasData = size(data) > 0 && sumBy(data, ({ thumbsUp, thumbsDown }) => thumbsUp + thumbsDown) > 0;

  return (
    <ChartCard
      title="Thumbs Up vs Thumbs Down Trend"
      description="Feedback count per day"
      isLoading={isLoading}
      hasData={hasData}
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart data={data} margin={CHART_MARGIN} barGap={2}>
          <CartesianGrid vertical={false} stroke={GRID_STROKE} />
          <XAxis
            dataKey="date"
            tickFormatter={formatAxisDate}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: AXIS_STROKE }}
            minTickGap={24}
          />
          <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
          <Tooltip
            cursor={{ fill: HOVER_CURSOR_FILL }}
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload as AiccFeedbackDailyPoint | undefined;
              if (!active || !point) {
                return null;
              }

              return (
                <TooltipBox
                  title={formatTooltipDate(point.date)}
                  rows={[
                    { label: "👍 Thumbs Up", value: String(point.thumbsUp), color: CHART_COLORS.thumbsUp },
                    { label: "👎 Thumbs Down", value: String(point.thumbsDown), color: CHART_COLORS.thumbsDown },
                    { label: "Total", value: String(point.thumbsUp + point.thumbsDown) },
                  ]}
                />
              );
            }}
          />
          <Legend
            iconType="square"
            iconSize={10}
            height={48}
            formatter={(value) => <span className={styles.legendLabel}>{value}</span>}
            wrapperStyle={{ fontSize: 12, paddingTop: 8, lineHeight: "20px" }}
          />
          {SERIES.map(({ dataKey, name, color }) => (
            <Bar
              key={dataKey}
              dataKey={dataKey}
              name={name}
              fill={color}
              maxBarSize={BAR_MAX_SIZE}
              radius={BAR_RADIUS}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export default TrendChart;
